import BigNumber from 'bignumber.js';

import { E勘定科目カテゴリ, E税区分, ECF集計項目, E集計転記科目, E固変区分 } from '@/openapi/enum';
import { 勘定科目ListItemResponse } from '@/openapi/user';

// ------------------------------------------------------------------
// enum
// ------------------------------------------------------------------

/**
 * PLBSCF種別
 */
export enum PlbscfType {
  貸借対照表,
  損益計算書,
  CF計算書,
  CF計算書_計算式,
  変動損益,
  運転資金,
  FCF,
}
export enum 計算Type {
  勘定科目Sum,
  codeSum,
  codeUnique,
}
/**
 * 行の色分け
 * （cssのクラス名を設定）
 */
export enum RowCssClass {
  titleRow, // タイトル行
  specialHeaderRow, // 特殊行
  headerRow, // 大分類
  subHeaderRow, // 中分類
  itemRow, // 勘定科目（色付き）
  diffRow, // 差分用
  greyRow, // グレー行
  cfSubHeaderRow, // 科目計　CF計算書用
  cfHeaderRow, // 科目計比較　CF計算書用
  noClassRow, // 色なし 要約表示用
}
/**
 * 勘定科目の検索用カラム名
 */
export type 勘定科目ColumnKey = keyof Pick<
  勘定科目ListItemResponse,
  'c大分類' | 'c中分類' | 'c勘定科目' | 'cCF集計項目' | 'c集計転記科目' | 'c固変区分' | 'c税区分'
>;

export enum ValueType {
  calcValue,
  calcValueReadonly,
  勘定科目,
  noValue,
}

// ------------------------------------------------------------------
// type
// ------------------------------------------------------------------

/**
 * 一行の設定
 */
export type PlbscfListItemConfig = {
  label: {
    category?: string;
    name: string;
    hierarchy: 0 | 1; // 階層
  };
  rowCssClass?: RowCssClass;
  valueType: ValueType;
  filter?: 勘定科目Filter | CodeFilter;
  blankColumns?: number[]; // 列のindex
  unitNotYen?: string; // 金額以外の場合は要設定（単位なしの場合は空文字）
  勘定科目カテゴリ?: E勘定科目カテゴリ; // valueType: 勘定科目以外で使用（現状製造原価のみ）
  // 要約表示時に表示する場合に設定
  // （ただしvalueType が noValue または calcValueReadonly で rowCssClass が変更ない場合は設定不要）
  rowCssClassIfSimple?:
    | RowCssClass.subHeaderRow
    | RowCssClass.specialHeaderRow
    | RowCssClass.itemRow
    | RowCssClass.noClassRow;
};
/**
 * 検索条件
 */
export type 勘定科目Filter = {
  カテゴリs?: E勘定科目カテゴリ[];
  column: 勘定科目ColumnKey;
  values: (string | ECF集計項目 | E集計転記科目 | E固変区分 | E税区分)[];
};
export type CodeFilter = {
  code: string;
  required: boolean;
};
/**
 * 検索条件
 */
export type CodeSumFilter = {
  code: string;
  coefficient?: number; // 係数（小数不可、設定なしの場合は1として扱う）
  beforeYearCount?: number; // 〇期前のデータを指定（小数不可、設定なしの場合は当期のデータを使用する）
};
export type CodeUniqueDivision = {
  numerator: string;
  denominator: string;
  isPercent: boolean; // ％表記（100倍）するか
  roundingMode?: BigNumber.RoundingMode; // 切上げ、切捨て、四捨五入
  decimalPlaces: number; // 表示したい小数の桁数（指定した数字＋１位を端数処理）
};
/**
 * 計算Codeと計算設定を紐づけるMap
 */
type CalcProcessMap = Map<string, CalcProcess>;
/**
 * 計算設定
 */
export type CalcProcess = {
  config: {
    計算Type: 計算Type;
    勘定科目Sum?: 勘定科目Filter; // 計算Type.勘定科目Sum の場合、必ず設定
    codeSum?: CodeSumFilter[]; // 計算Type.codeSum の場合、必ず設定
    // 計算Type.codeUnique のうち、一定の法則性がある除算をする場合、必ず設定
    codeUnique?: {
      division: CodeUniqueDivision;
    };
  };
};

// ------------------------------------------------------------------
// const
// ------------------------------------------------------------------

/**
 * 画面表示　行データ作成用　貸借対照表
 */
export const CONFIGS_貸借対照表: PlbscfListItemConfig[] = [
  {
    label: {
      name: '流動資産',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N6',
      required: true,
    },
  },
  {
    label: {
      name: '現金及び預金　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N1',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['現金及び預金'],
    },
  },
  {
    label: {
      name: '売上債権　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N2',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['売上債権'],
    },
  },
  {
    label: {
      name: '棚卸資産　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N3',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['棚卸資産'],
    },
  },
  {
    label: {
      name: '有価証券　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N4',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['有価証券'],
    },
  },
  {
    label: {
      name: 'その他の流動資産　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N5',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['その他の流動資産'],
    },
  },
  {
    label: {
      name: '固定資産',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N10',
      required: true,
    },
  },
  {
    label: {
      name: '有形固定資産　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N7',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['有形固定資産'],
    },
  },
  {
    label: {
      name: '無形固定資産　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N8',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['無形固定資産'],
    },
  },
  {
    label: {
      name: '投資その他の資産　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N9',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['投資その他の資産'],
    },
  },
  {
    label: {
      name: '繰延資産',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N11',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c大分類',
      values: ['繰延資産'],
    },
  },
  {
    label: {
      name: '資産の部　合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N12',
      required: true,
    },
  },
  {
    label: {
      name: '流動負債',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N15',
      required: true,
    },
  },
  {
    label: {
      name: '仕入債務　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N13',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['仕入債務'],
    },
  },
  {
    label: {
      name: 'その他の流動負債　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N14',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['その他の流動負債'],
    },
  },
  {
    label: {
      name: '固定負債',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N16',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c大分類',
      values: ['固定負債'],
    },
  },
  {
    label: {
      name: '負債の部　合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N17',
      required: true,
    },
  },
  {
    label: {
      name: '株主資本',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N24',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c勘定科目',
      values: ['資本金'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c勘定科目',
      values: ['新株式申込証拠金'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '資本剰余金　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N20',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.itemRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['資本剰余金'],
    },
  },
  {
    label: {
      name: '利益剰余金　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N21',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.itemRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c中分類',
      values: ['利益剰余金'],
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c勘定科目',
      values: ['自己株式'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c勘定科目',
      values: ['自己株式申込証拠金'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '評価･換算差額等',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N25',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c大分類',
      values: ['評価･換算差額等'],
    },
  },
  {
    label: {
      name: '',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c勘定科目',
      values: ['新株予約権'],
    },
    rowCssClassIfSimple: RowCssClass.itemRow,
  },
  {
    label: {
      name: '純資産の部　合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N27',
      required: true,
    },
  },
  {
    label: {
      name: '負債及び純資産　合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N28',
      required: true,
    },
  },
  {
    label: {
      name: '資産の部・負債及び純資産の部【差異】',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.diffRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N29',
      required: true,
    },
  },
];
/**
 * 画面表示　行データ作成用　損益計算書
 */
export const CONFIGS_損益計算書: PlbscfListItemConfig[] = [
  {
    label: {
      name: '損益計算書',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.titleRow,
    valueType: ValueType.noValue,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c大分類',
      values: ['売上高'],
    },
  },
  {
    label: {
      name: '売上高　合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N201',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c中分類',
      values: ['期首商品棚卸高'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c中分類',
      values: ['当期商品仕入'],
    },
  },
  {
    label: {
      name: '合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N203',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c中分類',
      values: ['他勘定振替高（商）'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c集計転記科目',
      values: [E集計転記科目.期末商品棚卸高],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '商品売上原価',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N206',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.itemRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c中分類',
      values: ['期首製品棚卸高'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '当期製品製造原価',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N208',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N209',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.itemRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c中分類',
      values: ['他勘定振替高（製）'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c集計転記科目',
      values: [E集計転記科目.期末製品棚卸高],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '製品売上原価',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N212',
      required: false,
    },
    rowCssClassIfSimple: RowCssClass.itemRow,
  },
  {
    label: {
      name: '売上原価',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N213',
      required: true,
    },
  },
  {
    label: {
      name: '売上総利益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N214',
      required: true,
    },
  },
  {
    label: {
      name: '売上総利益率（％）',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N215',
      required: true,
    },
    unitNotYen: '%',
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c大分類',
      values: ['販売管理費'],
    },
  },
  {
    label: {
      name: '販売費及び一般管理費',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N216',
      required: true,
    },
  },
  {
    label: {
      name: '営業利益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N217',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c大分類',
      values: ['営業外収益'],
    },
  },
  {
    label: {
      name: '営業外収益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N218',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c大分類',
      values: ['営業外費用'],
    },
  },
  {
    label: {
      name: '営業外費用',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N219',
      required: true,
    },
  },
  {
    label: {
      name: '経常利益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N220',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c大分類',
      values: ['特別利益'],
    },
  },
  {
    label: {
      name: '特別利益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N221',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c大分類',
      values: ['特別損失'],
    },
  },
  {
    label: {
      name: '特別損失',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N222',
      required: true,
    },
  },
  {
    label: {
      name: '税引前当期純利益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N223',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益],
      column: 'c大分類',
      values: ['法人税等'],
    },
  },
  {
    label: {
      name: '法人税等',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N224',
      required: true,
    },
  },
  {
    label: {
      name: '税引後当期純利益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N225',
      required: true,
    },
  },
  {
    label: {
      name: '製造原価報告書',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.titleRow,
    valueType: ValueType.noValue,
    勘定科目カテゴリ: E勘定科目カテゴリ.製造原価,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.製造原価],
      column: 'c中分類',
      values: ['期首材料棚卸高'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.製造原価],
      column: 'c中分類',
      values: ['当期材料費'],
    },
  },
  {
    label: {
      name: '合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.subHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N102',
      required: false,
    },
    勘定科目カテゴリ: E勘定科目カテゴリ.製造原価,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.製造原価],
      column: 'c集計転記科目',
      values: [E集計転記科目.期末材料棚卸高],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '材料費',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N104',
      required: true,
    },
    勘定科目カテゴリ: E勘定科目カテゴリ.製造原価,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.製造原価],
      column: 'c大分類',
      values: ['労務費'],
    },
  },
  {
    label: {
      name: '労務費',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N105',
      required: true,
    },
    勘定科目カテゴリ: E勘定科目カテゴリ.製造原価,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.製造原価],
      column: 'c大分類',
      values: ['製造経費'],
    },
  },
  {
    label: {
      name: '製造経費',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N106',
      required: true,
    },
    勘定科目カテゴリ: E勘定科目カテゴリ.製造原価,
  },
  {
    label: {
      name: '当期総製造費用',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N107',
      required: true,
    },
    勘定科目カテゴリ: E勘定科目カテゴリ.製造原価,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.製造原価],
      column: 'c中分類',
      values: ['期首仕掛品棚卸高'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N109',
      required: true,
    },
    勘定科目カテゴリ: E勘定科目カテゴリ.製造原価,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.製造原価],
      column: 'c集計転記科目',
      values: [E集計転記科目.期末仕掛品棚卸高],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.製造原価],
      column: 'c中分類',
      values: ['他勘定振替高（仕）'],
    },
    rowCssClassIfSimple: RowCssClass.noClassRow,
  },
  {
    label: {
      name: '当期製品製造原価',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.specialHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N112',
      required: true,
    },
    勘定科目カテゴリ: E勘定科目カテゴリ.製造原価,
  },
];
/**
 * 画面表示　行データ作成用　CF計算書
 */
export const CONFIGS_CF計算書: PlbscfListItemConfig[] = [
  {
    label: {
      category: 'I.',
      name: '営業活動によるキャッシュフロー',
      hierarchy: 0,
    },
    valueType: ValueType.noValue,
    blankColumns: [0],
  },
  {
    label: {
      name: '税引前当期純利益',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N401',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '減価償却費',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N402',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '貸倒引当金の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N403',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '受取利息及び受取配当金',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N404',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '支払利息等',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N405',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '特別利益調整',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N406',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '特別損失調整',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N407',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '売上債権の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N408',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '棚卸資産の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N409',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '繰延資産の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N410',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '仕入債務の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N411',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: 'その他の流動資産の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N412',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: 'その他の流動負債の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N413',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '小計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N414',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '受取利息及び配当金の受取額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N415',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '利息等の支払額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N416',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '法人税等の支払額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N417',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N418',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      category: 'II.',
      name: '投資活動によるキャッシュフロー',
      hierarchy: 0,
    },
    valueType: ValueType.noValue,
    blankColumns: [0],
  },
  {
    label: {
      name: '有価証券の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N419',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '固定資産の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N420',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '投資その他の資産の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N421',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '貸付金の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N422',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N423',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: 'FCF',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N437',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      category: 'III.',
      name: '財務活動によるキャッシュフロー',
      hierarchy: 0,
    },
    valueType: ValueType.noValue,
    blankColumns: [0],
  },
  {
    label: {
      name: '短期借入金の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N424',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '長期借入金の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N425',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '社債の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N426',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: 'その他の固定負債の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N427',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '資本金の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N428',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '自己株式の増減額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N429',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '配当金の支払額',
      hierarchy: 1,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N430',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N431',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      category: 'IV.',
      name: '現金及び現金同等物の増減額',
      hierarchy: 0,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N432',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      category: 'V.',
      name: '現金及び現金同等物の期首残高',
      hierarchy: 0,
    },
    valueType: ValueType.calcValue,
    filter: {
      code: 'N433',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      category: 'VI.',
      name: '現金及び現金同等物の期末残高',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N434',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '決算書上の現預金残高',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.diffRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N435',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: 'CF計算書-決算書　差額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.diffRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N436',
      required: true,
    },
    blankColumns: [0],
  },
];
/**
 * 画面表示　行データ作成用　CF計算書（計算式）
 */
export const CONFIGS_CF計算書_計算式: PlbscfListItemConfig[] = [
  {
    label: {
      name: 'CF計算書（計算式）',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.titleRow,
    valueType: ValueType.noValue,
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.減価償却費],
    },
  },
  {
    label: {
      name: '減価償却費',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N346',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.貸倒引当金],
    },
  },
  {
    label: {
      name: '貸倒引当金の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N301',
      required: true,
    },
  },
  {
    label: {
      name: '貸倒引当金の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N302',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.受取利息及び受取配当金],
    },
  },
  {
    label: {
      name: '受取利息及び受取配当金',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N347',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.支払利息等],
    },
  },
  {
    label: {
      name: '支払利息等',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N348',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.特別利益調整],
    },
  },
  {
    label: {
      name: '特別利益調整',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N349',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.特別損失調整],
    },
  },
  {
    label: {
      name: '特別損失調整',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N350',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.売上債権],
    },
  },
  {
    label: {
      name: '売上債権の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N303',
      required: true,
    },
  },
  {
    label: {
      name: '売上債権の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N304',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.棚卸資産],
    },
  },
  {
    label: {
      name: '棚卸資産の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N305',
      required: true,
    },
  },
  {
    label: {
      name: '棚卸資産の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N306',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.繰延資産],
    },
  },
  {
    label: {
      name: '繰延資産の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N307',
      required: true,
    },
  },
  {
    label: {
      name: '繰延資産の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N308',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.仕入債務],
    },
  },
  {
    label: {
      name: '仕入債務の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N309',
      required: true,
    },
  },
  {
    label: {
      name: '仕入債務の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N310',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.その他の流動資産],
    },
  },
  {
    label: {
      name: 'その他の流動資産の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N311',
      required: true,
    },
  },
  {
    label: {
      name: 'その他の流動資産の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N312',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.その他の流動負債],
    },
  },
  {
    label: {
      name: 'その他の流動負債の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N313',
      required: true,
    },
  },
  {
    label: {
      name: 'その他の流動負債の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N314',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '前期末　未払法人税等',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N315',
      required: true,
    },
  },
  {
    label: {
      name: '当期発生法人税等',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N316',
      required: true,
    },
  },
  {
    label: {
      name: '今期末　未払法人税等',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N317',
      required: true,
    },
  },
  {
    label: {
      name: '法人税等の支払額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N318',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.有価証券],
    },
  },
  {
    label: {
      name: '有価証券の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N319',
      required: true,
    },
  },
  {
    label: {
      name: '有価証券の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N320',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.有形固定資産],
    },
  },
  {
    label: {
      name: '有形固定資産の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N321',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.無形固定資産],
    },
  },
  {
    label: {
      name: '無形固定資産の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N322',
      required: true,
    },
  },
  {
    label: {
      name: '減価償却費',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N323',
      required: true,
    },
  },
  {
    label: {
      name: '有形・無形固定資産と減価償却費の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N324',
      required: true,
    },
  },
  {
    label: {
      name: '特別利益調整',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N325',
      required: false,
    },
  },
  {
    label: {
      name: '特別損失調整',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N326',
      required: false,
    },
  },
  {
    label: {
      name: '固定資産の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N327',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.投資その他の資産],
    },
  },
  {
    label: {
      name: '投資その他の資産の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N328',
      required: true,
    },
  },
  {
    label: {
      name: '投資その他の資産の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N329',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.貸付金],
    },
  },
  {
    label: {
      name: '貸付金の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N330',
      required: true,
    },
  },
  {
    label: {
      name: '貸付金の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N331',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.短期借入金],
    },
  },
  {
    label: {
      name: '短期借入金の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N332',
      required: true,
    },
  },
  {
    label: {
      name: '短期借入金の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N333',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.長期借入金],
    },
  },
  {
    label: {
      name: '長期借入金の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N334',
      required: true,
    },
  },
  {
    label: {
      name: '長期借入金の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N335',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.社債],
    },
  },
  {
    label: {
      name: '社債の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N336',
      required: true,
    },
  },
  {
    label: {
      name: '社債の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N337',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.その他の固定負債],
    },
  },
  {
    label: {
      name: 'その他の固定負債の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N338',
      required: true,
    },
  },
  {
    label: {
      name: 'その他の固定負債の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N339',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.資本金],
    },
  },
  {
    label: {
      name: '資本金の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N340',
      required: true,
    },
  },
  {
    label: {
      name: '資本金の増加額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N341',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.自己株式],
    },
  },
  {
    label: {
      name: '自己株式の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N342',
      required: true,
    },
  },
  {
    label: {
      name: '自己株式の増減額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N343',
      required: true,
    },
    blankColumns: [0],
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      column: 'cCF集計項目',
      values: [ECF集計項目.配当金の支払額],
    },
  },
  {
    label: {
      name: '配当金の合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfSubHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N344',
      required: true,
    },
  },
  {
    label: {
      name: '配当金の支払額',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.cfHeaderRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N345',
      required: true,
    },
    blankColumns: [0],
  },
];
/**
 * 画面表示　行データ作成用　変動損益
 */
export const CONFIGS_変動損益: PlbscfListItemConfig[] = [
  {
    label: {
      name: '売上高合計',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N501',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.損益, E勘定科目カテゴリ.製造原価],
      column: 'c固変区分',
      values: [E固変区分.変動費],
    },
  },
  {
    label: {
      name: '棚卸増減',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N503',
      required: true,
    },
  },
  {
    label: {
      name: '変動費　合計',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N504',
      required: true,
    },
  },
  {
    label: {
      name: '変動費率（％）',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N505',
      required: true,
    },
    unitNotYen: '%',
  },
  {
    label: {
      name: '限界利益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N506',
      required: true,
    },
  },
  {
    label: {
      name: '限界利益率（％）',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N507',
      required: true,
    },
    unitNotYen: '%',
  },
  {
    label: {
      name: '固定費',
      hierarchy: 1,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N508',
      required: true,
    },
  },
  {
    label: {
      name: '固定費率（％）',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N509',
      required: true,
    },
    unitNotYen: '%',
  },
  {
    label: {
      name: '経常利益',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N510',
      required: true,
    },
  },
  {
    label: {
      name: '経常利益率（％）',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N511',
      required: true,
    },
    unitNotYen: '%',
  },
  {
    label: {
      name: '損益分岐点売上高',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N512',
      required: true,
    },
  },
  {
    label: {
      name: '「売上高合計」-「損益分岐点売上高」（差異）',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.diffRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N513',
      required: true,
    },
  },
];
/**
 * 画面表示　行データ作成用　運転資金
 */
export const CONFIGS_運転資金: PlbscfListItemConfig[] = [
  {
    label: {
      name: '平均月商',
      hierarchy: 0,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N601',
      required: true,
    },
  },
  {
    label: {
      name: '所要運転資金',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N605',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c集計転記科目',
      values: [E集計転記科目.売上債権],
    },
  },
  {
    label: {
      name: '棚卸資産',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N603',
      required: true,
    },
  },
  {
    label: {
      name: '',
      hierarchy: 1,
    },
    valueType: ValueType.勘定科目,
    filter: {
      カテゴリs: [E勘定科目カテゴリ.貸借],
      column: 'c集計転記科目',
      values: [E集計転記科目.仕入債務],
    },
  },
  {
    label: {
      name: '回転期間（月）',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.headerRow,
    valueType: ValueType.noValue,
  },
  {
    label: {
      name: '売上債権',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N606',
      required: true,
    },
    unitNotYen: '',
  },
  {
    label: {
      name: '棚卸資産',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N607',
      required: true,
    },
    unitNotYen: '',
  },
  {
    label: {
      name: '仕入債務',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N608',
      required: true,
    },
    unitNotYen: '',
  },
  {
    label: {
      name: '収支差',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N609',
      required: true,
    },
    unitNotYen: '',
  },
];
/**
 * 画面表示　行データ作成用　FCF
 */
export const CONFIGS_FCF: PlbscfListItemConfig[] = [
  {
    label: {
      name: '経常利益',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N701',
      required: true,
    },
  },
  {
    label: {
      name: '減価償却費',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N702',
      required: true,
    },
  },
  {
    label: {
      name: '法人税等',
      hierarchy: 1,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N703',
      required: true,
    },
  },
  {
    label: {
      name: 'FCF：A',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N704',
      required: true,
    },
  },
  {
    label: {
      name: '金融機関に対する有利子負債合計：B',
      hierarchy: 0,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N705',
      required: true,
    },
  },
  {
    label: {
      name: '現預金：C',
      hierarchy: 0,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N706',
      required: true,
    },
  },
  {
    label: {
      name: '所要運転資金：D',
      hierarchy: 0,
    },
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N707',
      required: true,
    },
  },
  {
    label: {
      name: '債務償還年数：（B-C-D）／A（年）',
      hierarchy: 0,
    },
    rowCssClass: RowCssClass.itemRow,
    valueType: ValueType.calcValueReadonly,
    filter: {
      code: 'N708',
      required: true,
    },
    unitNotYen: '',
  },
];
/**
 * 計算結果格納用Map　貸借対照表
 */
export const CALC_PROCESS_MAP_貸借対照表: CalcProcessMap = new Map([
  [
    'N1',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['現金及び預金'],
        },
      },
    },
  ],
  [
    'N2',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['売上債権'],
        },
      },
    },
  ],
  [
    'N3',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['棚卸資産'],
        },
      },
    },
  ],
  [
    'N4',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['有価証券'],
        },
      },
    },
  ],
  [
    'N5',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['その他の流動資産'],
        },
      },
    },
  ],
  [
    'N6',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N1' }, { code: 'N2' }, { code: 'N3' }, { code: 'N4' }, { code: 'N5' }],
      },
    },
  ],
  [
    'N7',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['有形固定資産'],
        },
      },
    },
  ],
  [
    'N8',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['無形固定資産'],
        },
      },
    },
  ],
  [
    'N9',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['投資その他の資産'],
        },
      },
    },
  ],
  [
    'N10',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N7' }, { code: 'N8' }, { code: 'N9' }],
      },
    },
  ],
  [
    'N11',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c大分類',
          values: ['繰延資産'],
        },
      },
    },
  ],
  [
    'N12',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N6' }, { code: 'N10' }, { code: 'N11' }],
      },
    },
  ],
  [
    'N13',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['仕入債務'],
        },
      },
    },
  ],
  [
    'N14',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['その他の流動負債'],
        },
      },
    },
  ],
  [
    'N15',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N13' }, { code: 'N14' }],
      },
    },
  ],
  [
    'N16',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c大分類',
          values: ['固定負債'],
        },
      },
    },
  ],
  [
    'N17',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N15' }, { code: 'N16' }],
      },
    },
  ],
  [
    'N18',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c勘定科目',
          values: ['資本金'],
        },
      },
    },
  ],
  [
    'N19',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c勘定科目',
          values: ['新株式申込証拠金'],
        },
      },
    },
  ],
  [
    'N20',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['資本剰余金'],
        },
      },
    },
  ],
  [
    'N21',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c中分類',
          values: ['利益剰余金'],
        },
      },
    },
  ],
  [
    'N22',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c勘定科目',
          values: ['自己株式'],
        },
      },
    },
  ],
  [
    'N23',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c勘定科目',
          values: ['自己株式申込証拠金'],
        },
      },
    },
  ],
  [
    'N24',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N18' }, { code: 'N19' }, { code: 'N20' }, { code: 'N21' }, { code: 'N22' }, { code: 'N23' }],
      },
    },
  ],
  [
    'N25',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c大分類',
          values: ['評価･換算差額等'],
        },
      },
    },
  ],
  [
    'N26',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c勘定科目',
          values: ['新株予約権'],
        },
      },
    },
  ],
  [
    'N27',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N24' }, { code: 'N25' }, { code: 'N26' }],
      },
    },
  ],
  [
    'N28',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N17' }, { code: 'N27' }],
      },
    },
  ],
  [
    'N29',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N12' }, { code: 'N28', coefficient: -1 }],
      },
    },
  ],
]);
/**
 * 計算結果格納用Map　製造原価
 */
const CALC_PROCESS_MAP_製造原価: CalcProcessMap = new Map([
  [
    'N101',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.製造原価],
          column: 'c中分類',
          values: ['期首材料棚卸高'],
        },
      },
    },
  ],
  [
    'N113',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.製造原価],
          column: 'c中分類',
          values: ['当期材料費'],
        },
      },
    },
  ],
  [
    'N102',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N101' }, { code: 'N113' }],
      },
    },
  ],
  [
    'N103',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.製造原価],
          column: 'c集計転記科目',
          values: [E集計転記科目.期末材料棚卸高],
        },
      },
    },
  ],
  [
    'N104',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N102' }, { code: 'N103', coefficient: -1 }],
      },
    },
  ],
  [
    'N105',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.製造原価],
          column: 'c大分類',
          values: ['労務費'],
        },
      },
    },
  ],
  [
    'N106',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.製造原価],
          column: 'c大分類',
          values: ['製造経費'],
        },
      },
    },
  ],
  [
    'N107',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N104' }, { code: 'N105' }, { code: 'N106' }],
      },
    },
  ],
  [
    'N108',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.製造原価],
          column: 'c中分類',
          values: ['期首仕掛品棚卸高'],
        },
      },
    },
  ],
  [
    'N109',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N107' }, { code: 'N108' }],
      },
    },
  ],
  [
    'N110',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.製造原価],
          column: 'c集計転記科目',
          values: [E集計転記科目.期末仕掛品棚卸高],
        },
      },
    },
  ],
  [
    'N111',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.製造原価],
          column: 'c中分類',
          values: ['他勘定振替高（仕）'],
        },
      },
    },
  ],
  [
    'N112',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N109' }, { code: 'N110', coefficient: -1 }, { code: 'N111', coefficient: -1 }],
      },
    },
  ],
]);
/**
 * 計算結果格納用Map　損益計算書
 */
const CALC_PROCESS_MAP_損益計算書: CalcProcessMap = new Map([
  [
    'N201',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c大分類',
          values: ['売上高'],
        },
      },
    },
  ],
  [
    'N202',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c中分類',
          values: ['期首商品棚卸高'],
        },
      },
    },
  ],
  [
    'N226',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c中分類',
          values: ['当期商品仕入'],
        },
      },
    },
  ],
  [
    'N203',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N202' }, { code: 'N226' }],
      },
    },
  ],
  [
    'N204',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c中分類',
          values: ['他勘定振替高（商）'],
        },
      },
    },
  ],
  [
    'N205',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c集計転記科目',
          values: [E集計転記科目.期末商品棚卸高],
        },
      },
    },
  ],
  [
    'N206',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N203' }, { code: 'N204', coefficient: -1 }, { code: 'N205', coefficient: -1 }],
      },
    },
  ],
  [
    'N207',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c中分類',
          values: ['期首製品棚卸高'],
        },
      },
    },
  ],
  [
    'N208',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N112' }],
      },
    },
  ],
  [
    'N209',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N207' }, { code: 'N208' }],
      },
    },
  ],
  [
    'N210',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c中分類',
          values: ['他勘定振替高（製）'],
        },
      },
    },
  ],
  [
    'N211',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c集計転記科目',
          values: [E集計転記科目.期末製品棚卸高],
        },
      },
    },
  ],
  [
    'N212',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N209' }, { code: 'N210', coefficient: -1 }, { code: 'N211', coefficient: -1 }],
      },
    },
  ],
  [
    'N213',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N206' }, { code: 'N212' }],
      },
    },
  ],
  [
    'N214',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N201' }, { code: 'N213', coefficient: -1 }],
      },
    },
  ],
  [
    'N215',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: 'N214',
            denominator: 'N201',
            isPercent: true,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
  [
    'N216',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c大分類',
          values: ['販売管理費'],
        },
      },
    },
  ],
  [
    'N217',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N214' }, { code: 'N216', coefficient: -1 }],
      },
    },
  ],
  [
    'N218',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c大分類',
          values: ['営業外収益'],
        },
      },
    },
  ],
  [
    'N219',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c大分類',
          values: ['営業外費用'],
        },
      },
    },
  ],
  [
    'N220',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N217' }, { code: 'N218' }, { code: 'N219', coefficient: -1 }],
      },
    },
  ],
  [
    'N221',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c大分類',
          values: ['特別利益'],
        },
      },
    },
  ],
  [
    'N222',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c大分類',
          values: ['特別損失'],
        },
      },
    },
  ],
  [
    'N223',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N220' }, { code: 'N221' }, { code: 'N222', coefficient: -1 }],
      },
    },
  ],
  [
    'N224',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c大分類',
          values: ['法人税等'],
        },
      },
    },
  ],
  [
    'N225',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N223' }, { code: 'N224', coefficient: -1 }],
      },
    },
  ],
]);
/**
 * 計算結果格納用Map　CF計算書_計算式
 */
const CALC_PROCESS_MAP_CF計算書_計算式: CalcProcessMap = new Map([
  [
    'N301',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.貸倒引当金],
        },
      },
    },
  ],
  [
    'N302',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [
          { code: 'N301', coefficient: -1 },
          { code: 'N301', beforeYearCount: 1 },
        ],
      },
    },
  ],
  [
    'N303',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.売上債権],
        },
      },
    },
  ],
  [
    'N304',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N303' }, { code: 'N303', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N305',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.棚卸資産],
        },
      },
    },
  ],
  [
    'N306',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N305' }, { code: 'N305', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N307',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.繰延資産],
        },
      },
    },
  ],
  [
    'N308',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N307' }, { code: 'N307', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N309',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.仕入債務],
        },
      },
    },
  ],
  [
    'N310',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N309' }, { code: 'N309', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N311',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.その他の流動資産],
        },
      },
    },
  ],
  [
    'N312',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N311' }, { code: 'N311', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N313',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.その他の流動負債],
        },
      },
    },
  ],
  [
    'N314',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N313' }, { code: 'N313', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N315',
    {
      config: {
        計算Type: 計算Type.codeUnique,
      },
    },
  ],
  [
    'N316',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.当期発生法人税等],
        },
      },
    },
  ],
  [
    'N317',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.未払法人税等],
        },
      },
    },
  ],
  [
    'N318',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N315' }, { code: 'N316' }, { code: 'N317', coefficient: -1 }],
      },
    },
  ],
  [
    'N319',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.有価証券],
        },
      },
    },
  ],
  [
    'N320',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N319' }, { code: 'N319', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N321',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.有形固定資産],
        },
      },
    },
  ],
  [
    'N322',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.無形固定資産],
        },
      },
    },
  ],
  [
    'N328',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.投資その他の資産],
        },
      },
    },
  ],
  [
    'N329',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N328' }, { code: 'N328', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N330',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.貸付金],
        },
      },
    },
  ],
  [
    'N331',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N330' }, { code: 'N330', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N332',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.短期借入金],
        },
      },
    },
  ],
  [
    'N333',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N332' }, { code: 'N332', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N334',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.長期借入金],
        },
      },
    },
  ],
  [
    'N335',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N334' }, { code: 'N334', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N336',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.社債],
        },
      },
    },
  ],
  [
    'N337',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N336' }, { code: 'N336', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N338',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.その他の固定負債],
        },
      },
    },
  ],
  [
    'N339',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N338' }, { code: 'N338', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N340',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.資本金],
        },
      },
    },
  ],
  [
    'N341',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N340' }, { code: 'N340', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N342',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.自己株式],
        },
      },
    },
  ],
  [
    'N343',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [
          { code: 'N342', coefficient: -1 },
          { code: 'N342', beforeYearCount: 1 },
        ],
      },
    },
  ],
  [
    'N344',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.配当金の支払額],
        },
      },
    },
  ],
  [
    'N345',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N344' }, { code: 'N344', coefficient: -1, beforeYearCount: 1 }],
      },
    },
  ],
  [
    'N346',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.減価償却費],
        },
      },
    },
  ],
  [
    'N347',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.受取利息及び受取配当金],
        },
      },
    },
  ],
  [
    'N348',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.支払利息等],
        },
      },
    },
  ],
  [
    'N349',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.特別利益調整],
        },
      },
    },
  ],
  [
    'N350',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.特別損失調整],
        },
      },
    },
  ],
  [
    'N323',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N346' }],
      },
    },
  ],
  [
    'N324',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N321' }, { code: 'N322' }, { code: 'N323' }],
      },
    },
  ],
  [
    'N325',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N349' }],
      },
    },
  ],
  [
    'N326',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N350' }],
      },
    },
  ],
  [
    'N327',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [
          { code: 'N324' },
          { code: 'N321', coefficient: -1, beforeYearCount: 1 },
          { code: 'N322', coefficient: -1, beforeYearCount: 1 },
          { code: 'N325', coefficient: -1 },
          { code: 'N326' },
        ],
      },
    },
  ],
]);
/**
 * 計算結果格納用Map　CF計算書
 */
const CALC_PROCESS_MAP_CF計算書: CalcProcessMap = new Map([
  [
    'N401',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N223' }],
      },
    },
  ],
  [
    'N402',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N346' }],
      },
    },
  ],
  [
    'N403',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N302' }],
      },
    },
  ],
  [
    'N404',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N347', coefficient: -1 }],
      },
    },
  ],
  [
    'N405',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N348' }],
      },
    },
  ],
  [
    'N406',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N349', coefficient: -1 }],
      },
    },
  ],
  [
    'N407',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N350' }],
      },
    },
  ],
  [
    'N408',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N304', coefficient: -1 }],
      },
    },
  ],
  [
    'N409',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N306', coefficient: -1 }],
      },
    },
  ],
  [
    'N410',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N308', coefficient: -1 }],
      },
    },
  ],
  [
    'N411',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N310' }],
      },
    },
  ],
  [
    'N412',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N312', coefficient: -1 }],
      },
    },
  ],
  [
    'N413',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N314' }],
      },
    },
  ],
  [
    'N414',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [
          { code: 'N401' },
          { code: 'N402' },
          { code: 'N403' },
          { code: 'N404' },
          { code: 'N405' },
          { code: 'N406' },
          { code: 'N407' },
          { code: 'N408' },
          { code: 'N409' },
          { code: 'N410' },
          { code: 'N411' },
          { code: 'N412' },
          { code: 'N413' },
        ],
      },
    },
  ],
  [
    'N415',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          column: 'cCF集計項目',
          values: [ECF集計項目.受取利息及び受取配当金],
        },
      },
    },
  ],
  [
    'N416',
    {
      config: {
        計算Type: 計算Type.codeUnique,
      },
    },
  ],
  [
    'N417',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N318', coefficient: -1 }],
      },
    },
  ],
  [
    'N418',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N414' }, { code: 'N415' }, { code: 'N416' }, { code: 'N417' }],
      },
    },
  ],
  [
    'N419',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N320', coefficient: -1 }],
      },
    },
  ],
  [
    'N420',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N327', coefficient: -1 }],
      },
    },
  ],
  [
    'N421',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N329', coefficient: -1 }],
      },
    },
  ],
  [
    'N422',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N331', coefficient: -1 }],
      },
    },
  ],
  [
    'N423',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N419' }, { code: 'N420' }, { code: 'N421' }, { code: 'N422' }],
      },
    },
  ],
  [
    'N424',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N333' }],
      },
    },
  ],
  [
    'N425',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N335' }],
      },
    },
  ],
  [
    'N426',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N337' }],
      },
    },
  ],
  [
    'N427',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N339' }],
      },
    },
  ],
  [
    'N428',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N341' }],
      },
    },
  ],
  [
    'N429',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N343' }],
      },
    },
  ],
  [
    'N430',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N345' }],
      },
    },
  ],
  [
    'N431',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [
          { code: 'N424' },
          { code: 'N425' },
          { code: 'N426' },
          { code: 'N427' },
          { code: 'N428' },
          { code: 'N429' },
          { code: 'N430' },
        ],
      },
    },
  ],
  [
    'N432',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N418' }, { code: 'N423' }, { code: 'N431' }],
      },
    },
  ],
  [
    'N433',
    {
      config: {
        計算Type: 計算Type.codeUnique,
      },
    },
  ],
  [
    'N434',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N432' }, { code: 'N433' }],
      },
    },
  ],
  [
    'N435',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N1' }],
      },
    },
  ],
  [
    'N436',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N434' }, { code: 'N435', coefficient: -1 }],
      },
    },
  ],
  [
    'N437',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N418' }, { code: 'N423' }],
      },
    },
  ],
]);
/**
 * 計算結果格納用Map　変動損益
 */
const CALC_PROCESS_MAP_変動損益: CalcProcessMap = new Map([
  [
    'N501',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N201' }],
      },
    },
  ],
  [
    'N502',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益, E勘定科目カテゴリ.製造原価],
          column: 'c固変区分',
          values: [E固変区分.変動費],
        },
      },
    },
  ],
  [
    'N503',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [
          { code: 'N202' },
          { code: 'N205', coefficient: -1 },
          { code: 'N207' },
          { code: 'N211', coefficient: -1 },
          { code: 'N101' },
          { code: 'N103', coefficient: -1 },
          { code: 'N108' },
          { code: 'N110', coefficient: -1 },
        ],
      },
    },
  ],
  [
    'N504',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N502' }, { code: 'N503' }],
      },
    },
  ],
  [
    'N505',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: 'N504',
            denominator: 'N501',
            isPercent: true,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
  [
    'N506',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N501' }, { code: 'N504', coefficient: -1 }],
      },
    },
  ],
  [
    'N507',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: 'N506',
            denominator: 'N501',
            isPercent: true,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
  [
    'N508',
    {
      config: {
        計算Type: 計算Type.codeUnique,
      },
    },
  ],
  [
    'N509',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: 'N508',
            denominator: 'N501',
            isPercent: true,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
  [
    'N510',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N506' }, { code: 'N508', coefficient: -1 }],
      },
    },
  ],
  [
    'N511',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: 'N510',
            denominator: 'N501',
            isPercent: true,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
  [
    'N512',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: 'N508',
            denominator: 'N507',
            isPercent: false,
            decimalPlaces: 0,
          },
        },
      },
    },
  ],
  [
    'N513',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N501' }, { code: 'N512', coefficient: -1 }],
      },
    },
  ],
]);
/**
 * 計算結果格納用Map　運転資金
 */
const CALC_PROCESS_MAP_運転資金: CalcProcessMap = new Map([
  [
    'N601',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: 'N201',
            denominator: '',
            isPercent: false,
            decimalPlaces: 0,
          },
        },
      },
    },
  ],
  [
    'N602',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c集計転記科目',
          values: [E集計転記科目.売上債権],
        },
      },
    },
  ],
  [
    'N603',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c集計転記科目',
          values: [
            E集計転記科目.棚卸資産,
            E集計転記科目.棚卸資産PL期末商品,
            E集計転記科目.棚卸資産PL期末製品,
            E集計転記科目.棚卸資産CR期末材料,
            E集計転記科目.棚卸資産CR期末仕掛品,
          ],
        },
      },
    },
  ],
  [
    'N604',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c集計転記科目',
          values: [E集計転記科目.仕入債務],
        },
      },
    },
  ],
  [
    'N605',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N602' }, { code: 'N603' }, { code: 'N604', coefficient: -1 }],
      },
    },
  ],
  [
    'N606',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: '',
            denominator: 'N601',
            isPercent: false,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
  [
    'N607',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: 'N603',
            denominator: 'N601',
            isPercent: false,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
  [
    'N608',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: '',
            denominator: 'N601',
            isPercent: false,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
  [
    'N609',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N606' }, { code: 'N607' }, { code: 'N608', coefficient: -1 }],
      },
    },
  ],
]);
/**
 * 計算結果格納用Map　FCF
 */
const CALC_PROCESS_MAP_FCF: CalcProcessMap = new Map([
  [
    'N701',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N220' }],
      },
    },
  ],
  [
    'N702',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益, E勘定科目カテゴリ.製造原価],
          column: 'c集計転記科目',
          values: [E集計転記科目.減価償却費販管費, E集計転記科目.減価償却費製造原価],
        },
      },
    },
  ],
  [
    'N703',
    {
      config: {
        計算Type: 計算Type.codeUnique,
      },
    },
  ],
  [
    'N704',
    {
      config: {
        計算Type: 計算Type.codeUnique,
      },
    },
  ],
  [
    'N705',
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.貸借],
          column: 'c集計転記科目',
          values: [E集計転記科目.有利子負債短期借入金, E集計転記科目.有利子負債長期借入金],
        },
      },
    },
  ],
  [
    'N706',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N1' }],
      },
    },
  ],
  [
    'N707',
    {
      config: {
        計算Type: 計算Type.codeSum,
        codeSum: [{ code: 'N605' }],
      },
    },
  ],
  [
    'N708',
    {
      config: {
        計算Type: 計算Type.codeUnique,
        codeUnique: {
          division: {
            numerator: '',
            denominator: 'N704',
            isPercent: false,
            roundingMode: BigNumber.ROUND_HALF_UP,
            decimalPlaces: 1,
          },
        },
      },
    },
  ],
]);
/**
 * 計算結果格納用Map　その他
 * ※Plbscf以外の画面での表示のみに使用し、DBには入れない想定
 */
const CALC_PROCESS_MAP_その他: CalcProcessMap = new Map([
  [
    E税区分.課税売上,
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c税区分',
          values: [E税区分.課税売上],
        },
      },
    },
  ],
  [
    E税区分.課税売上返還,
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益],
          column: 'c税区分',
          values: [E税区分.課税売上返還],
        },
      },
    },
  ],
  [
    E税区分.課税仕入,
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益, E勘定科目カテゴリ.製造原価],
          column: 'c税区分',
          values: [E税区分.課税仕入],
        },
      },
    },
  ],
  [
    E税区分.課税仕入返還,
    {
      config: {
        計算Type: 計算Type.勘定科目Sum,
        勘定科目Sum: {
          カテゴリs: [E勘定科目カテゴリ.損益, E勘定科目カテゴリ.製造原価],
          column: 'c税区分',
          values: [E税区分.課税仕入返還],
        },
      },
    },
  ],
]);
/**
 * 計算結果格納用Map
 */
export const CALC_PROCESS_MAP: CalcProcessMap = new Map([
  ...CALC_PROCESS_MAP_貸借対照表,
  ...CALC_PROCESS_MAP_製造原価,
  ...CALC_PROCESS_MAP_損益計算書,
  ...CALC_PROCESS_MAP_CF計算書_計算式,
  ...CALC_PROCESS_MAP_CF計算書,
  ...CALC_PROCESS_MAP_変動損益,
  ...CALC_PROCESS_MAP_運転資金,
  ...CALC_PROCESS_MAP_FCF,
  ...CALC_PROCESS_MAP_その他,
]);
/**
 * 月次用　計算結果格納用Map
 */
export const 月次_CALC_PROCESS_MAP: CalcProcessMap = new Map([
  ...CALC_PROCESS_MAP_貸借対照表,
  ...CALC_PROCESS_MAP_製造原価,
  ...CALC_PROCESS_MAP_損益計算書,
  ...CALC_PROCESS_MAP_その他,
]);
/**
 * 計画損益用　計算結果格納用Map
 */
export const 損益_CALC_PROCESS_MAP: CalcProcessMap = new Map([
  ...CALC_PROCESS_MAP_製造原価,
  ...CALC_PROCESS_MAP_損益計算書,
  ...CALC_PROCESS_MAP_その他,
]);
